import { lazyWithPreload } from 'react-lazy-with-preload';
import { Navigate, useParams } from 'react-router-dom';

const AILayout = lazyWithPreload(() => import('./layouts/camera-ai/AILayout'));
const IntegrationLayout = lazyWithPreload(() => import('./layouts/camera-integration/IntegrationLayout'));
const LiveLapseLayout = lazyWithPreload(() => import('./layouts/livelapse/LiveLapseLayout'));
const EquipmentTrackerLayout = lazyWithPreload(() => import('./layouts/equipment-tracker/EquipmentTrackerLayout'));
const ProjectDetailLayout = lazyWithPreload(() => import('./layouts/project-detail/ProjectDetailLayout'));
const CameraLayout = lazyWithPreload(() => import('./layouts/camera/CameraLayout'));
const SiteWalkLayout = lazyWithPreload(() => import('./layouts/site-walk/SiteWalkLayout'));
const CameraCctvLayout = lazyWithPreload(() => import('./layouts/camera-cctv/CameraCctvLayout'));
const AerialLayout = lazyWithPreload(() => import('./layouts/aerial/AerialLayout'));

const AINotificationPage = lazyWithPreload(() => import('./pages/ai-notifications/Page'));
const DashboardPage = lazyWithPreload(() => import('./pages/dashboard/Page'));
const ProjectListPage = lazyWithPreload(() => import('./pages/project-list/Page'));
const ProjectReportPage = lazyWithPreload(() => import('./pages/project-report/Page'));
const BimModelListPage = lazyWithPreload(() => import('./pages/bim-model-list/Page'));
const BimModelDetailPage = lazyWithPreload(() => import('./pages/bim-model-detail/Page'));
const CameraListPage = lazyWithPreload(() => import('./pages/camera-list/Page'));
const CctvCameraListPage = lazyWithPreload(() => import('./pages/cctv-list/Page'));
const CctvCameraRecordingListPage = lazyWithPreload(() => import('./pages/cctv-recording/Page'));
const CctvCameraRecordingDetailPage = lazyWithPreload(() => import('./pages/cctv-recording-detail/Page'));
const DroneListPage = lazyWithPreload(() => import('./pages/drone-list/Page'));
const SiteGalleryListPage = lazyWithPreload(() => import('./pages/site-gallery-list/Page'));
const EquipmentTrackerListPage = lazyWithPreload(() => import('./pages/equipment-tracker-list/Page'));
const ProjectLinkListPage = lazyWithPreload(() => import('./pages/project-links/Page'));
const AerialListPage = lazyWithPreload(() => import('./pages/aerial-list/Page'));
const AerialDetailPage = lazyWithPreload(() => import('./pages/aerial-detail/Page'));
const AerialLocalTilesPage = lazyWithPreload(() => import('./pages/aerial-local-tiles/Page'));
const AerialComparePage = lazyWithPreload(() => import('./pages/aerial-compare/Page'));
const TerrainListPage = lazyWithPreload(() => import('./pages/aerial-terrain-list/Page'));
const AerialTerrainPage = lazyWithPreload(() => import('./pages/aerial-terrain/Page'));

const CameraDetailPage = lazyWithPreload(() => import('./pages/camera-detail/Page'));
const CameraBimViewerPage = lazyWithPreload(() => import('./pages/camera-bim-viewer/Page'));
const AlignBimViewerPage = lazyWithPreload(() => import('./pages/align-camera-bim-viewer/Page'));
const CctvDetailPage = lazyWithPreload(() => import('./pages/cctv-detail/Page'));
const SiteWalkDetailPage = lazyWithPreload(() => import('./pages/site-walk-detail/Page'));
const SiteWalkComparePage = lazyWithPreload(() => import('./pages/site-walk-compare/Page'));
const SiteWalkBimComparePage = lazyWithPreload(() => import('./pages/site-walk-bim-compare/Page'));

const DroneDetailPage = lazyWithPreload(() => import('./pages/drone-detail/Page'));
const SiteGalleryDetailPage = lazyWithPreload(() => import('./pages/site-gallery-detail/Page'));
const SiteWalkListPage = lazyWithPreload(() => import('./pages/site-walk-list/Page'));

const CameraComparePage = lazyWithPreload(() => import('./pages/camera-compare/Page'));
const CameraImageProgressLineSharePage = lazyWithPreload(() => import('./pages/camera-image-progress-line-share/Page'));
const CameraImageProcoreSharePage = lazyWithPreload(() => import('./pages/camera-image-procore-share/Page'));
const CameraImagePlanGridSharePage = lazyWithPreload(() => import('./pages/camera-image-plan-grid-share/Page'));
const CameraImageAconexSharePage = lazyWithPreload(() => import('./pages/camera-image-aconex-share/Page'));
const CameraImageSocialSharePage = lazyWithPreload(() => import('./pages/camera-image-social-share/Page'));
const CameraImageCommentPage = lazyWithPreload(() => import('./pages/camera-image-comment/Page'));
const ProgressSliderPage = lazyWithPreload(() => import('./pages/progress-slider/Page'));
const ForwardingHookListPage = lazyWithPreload(() => import('./pages/forwarding-hooks/Page'));
const ReportPage = lazyWithPreload(() => import('./pages/report/Page'));
const ApiIntegrationPage = lazyWithPreload(() => import('./pages/api-integration/Page'));
const AutodeskIntegrationPage = lazyWithPreload(() => import('./pages/autodesk-integration/Page'));
const IFrameIntegrationPage = lazyWithPreload(() => import('./pages/iframe-integration/Page'));
const ProcoreIntegrationPage = lazyWithPreload(() => import('./pages/procore-integration/Page'));

const LiveLapseListPage = lazyWithPreload(() => import('./pages/livelapse-list/Page'));
const LiveLapseComparePage = lazyWithPreload(() => import('./pages/livelapse-compare/LiveLapseComparePage'));
const LiveLapseDetailPage = lazyWithPreload(() => import('./pages/livelapse-detail/Page'));

const AIActivityAnalysisPage = lazyWithPreload(() => import('./pages/ai-activity-analysis/Page'));
const AIActivityProgressSliderPage = lazyWithPreload(() => import('./pages/ai-activity-progress-slider/Page'));
const AISafetyAnalysisPage = lazyWithPreload(() => import('./pages/ai-safety-analysis/Page'));
const AISafetyProgressSliderPage = lazyWithPreload(() => import('./pages/ai-safety-progress-slider/Page'));

const AIEquipmentTrackingPage = lazyWithPreload(() => import('./pages/ai-equipemt-tracking/page'));
const AIEquipmentTrackingSliderPage = lazyWithPreload(() => import('./pages/ai-equipment-tracking-slider/Page'));
const ProjectLiveLapsePage = lazyWithPreload(() => import('./pages/project-livelapse-list/Page'));
const ProjectLiveLapseDetailPage = lazyWithPreload(() => import('./pages/project-livelapse-detail/Page'));

const DroneDetailRouteRedirect = () => {
    const { projectId, droneId } = useParams();

    return <Navigate to={`/projects/${projectId}/aerial/drone/${droneId}`} replace />;
};

const OrthomosaicRouteRedirect = () => {
    const { projectId, aerialId, orthomosaicId } = useParams();
    const id = orthomosaicId || aerialId;

    return <Navigate to={`/projects/${projectId}/aerial/orthomosaic/${id}`} replace />;
};

const Routes = [
    {
        path: 'dashboard',
        element: <DashboardPage />,
    },
    {
        path: 'projects/:projectId',
        element: <ProjectListPage />,
    },
    {
        path: 'projects/:projectId',
        element: <ProjectDetailLayout />,
        children: [
            {
                path: 'cameras',
                element: <CameraListPage />,
            },
            {
                path: 'drones',
                element: <Navigate to="../aerial/drone" replace />,
            },
            {
                path: 'live-cameras',
                element: <CctvCameraListPage />,
            },
            {
                path: 'floor-plans',
                element: <SiteWalkListPage />,
            },
            {
                path: 'site-gallery',
                element: <SiteGalleryListPage />,
            },
            {
                path: 'equipment-trackers',
                element: <EquipmentTrackerLayout />,
                children: [
                    {
                        index: true,
                        element: <EquipmentTrackerListPage />,
                    },
                    {
                        path: 'historical',
                        element: <EquipmentTrackerListPage />,
                    },
                    {
                        path: 'map',
                        element: <EquipmentTrackerListPage />,
                    },
                    {
                        path: 'costs',
                        element: <EquipmentTrackerListPage />,
                    },
                    {
                        path: 'rentals',
                        element: <EquipmentTrackerListPage />,
                    },
                    {
                        path: 'operators',
                        element: <EquipmentTrackerListPage />,
                    },
                    {
                        path: 'alerts',
                        element: <EquipmentTrackerListPage />,
                    },
                ],
            },
            {
                path: 'links',
                element: <ProjectLinkListPage />,
            },
            {
                path: 'aerial',
                element: <Navigate to="../aerial/orthomosaic" replace />,
            },
            {
                path: 'aerial/drone',
                element: <DroneListPage />,
            },
            {
                path: 'aerial/terrain',
                element: <TerrainListPage />,
            },
            {
                path: 'aerial/orthomosaic',
                element: <AerialListPage />,
            },
        ],
    },

    {
        path: 'projects/:projectId/reports',
        element: <ProjectReportPage />,
    },
    {
        path: 'projects/:projectId/bim-models',
        element: <BimModelListPage />,
    },
    {
        path: 'projects/:projectId/bim-models/:bimId',
        element: <BimModelDetailPage />,
    },
    {
        path: 'projects/:projectId/liveLapse',
        // element: <ProjectReportPage />,
        element: <ProjectLiveLapsePage />,
    },
    {
        path: 'projects/:projectId/liveLapse/:liveLapseId/play',
        element: <ProjectLiveLapseDetailPage />,
    },
    // {
    //     path: 'projects/:projectId/liveLapse/:liveLapseId/edit',
    //     element: <TimelineEditor />,
    // },
    {
        path: 'projects/:projectId/cameras/:cameraId',
        element: <CameraLayout />,
        children: [
            {
                index: true,
                element: <CameraDetailPage />,
            },

            {
                path: 'bim-viewer',
                element: <CameraBimViewerPage />,
            },
            {
                path: 'align-bim-viewer',
                element: <AlignBimViewerPage />,
            },
            {
                path: 'images/:imageName/social-share',
                element: <CameraImageSocialSharePage />,
            },
            {
                path: 'images/:imageName/progress-line-share',
                element: <CameraImageProgressLineSharePage />,
            },
            {
                path: 'images/:imageName/procore-share',
                element: <CameraImageProcoreSharePage />,
            },
            {
                path: 'images/:imageName/plan-grid-share',
                element: <CameraImagePlanGridSharePage />,
            },
            {
                path: 'images/:imageName/aconex-share',
                element: <CameraImageAconexSharePage />,
            },
            {
                path: 'images/:imageName/comments',
                element: <CameraImageCommentPage />,
            },
            {
                path: 'livelapse',
                element: <LiveLapseLayout />,
                children: [
                    {
                        index: true,
                        element: <LiveLapseListPage />,
                    },
                    {
                        path: 'compare',
                        element: <LiveLapseComparePage />,
                    },
                ],
            },
            {
                path: 'livelapse/:liveLapseId/play',
                element: <LiveLapseDetailPage />,
            },
            {
                path: 'progress-slider',
                element: <ProgressSliderPage />,
            },
            {
                path: 'compare',
                element: <CameraComparePage />,
            },
            {
                path: 'reports',
                element: <ReportPage />,
            },

            {
                path: 'integrations',
                element: <IntegrationLayout />,
                children: [
                    {
                        index: true,
                        element: <ForwardingHookListPage />,
                    },
                    {
                        path: 'api',
                        element: <ApiIntegrationPage />,
                    },
                    {
                        path: 'autodesk',
                        element: <AutodeskIntegrationPage />,
                    },
                    {
                        path: 'iframe',
                        element: <IFrameIntegrationPage />,
                    },
                    {
                        path: 'procore',
                        element: <ProcoreIntegrationPage />,
                    },
                ],
            },
            {
                path: 'ai',
                element: <AILayout />,
                children: [
                    {
                        path: 'public',
                        element: <AIActivityAnalysisPage />,
                    },
                    {
                        path: 'safety',
                        element: <AISafetyAnalysisPage />,
                    },
                    {
                        path: 'equipment',
                        element: <AIEquipmentTrackingPage />,
                    },
                    {
                        path: 'notification',
                        element: <AINotificationPage />,
                    },
                ],
            },
            {
                path: 'ai/public/images',
                element: <AIActivityProgressSliderPage />,
            },
            {
                path: 'ai/safety/images',
                element: <AISafetyProgressSliderPage />,
            },

            {
                path: 'ai/equipment/images',
                element: <AIEquipmentTrackingSliderPage />,
            },
        ],
    },
    {
        path: 'projects/:projectId/floor-plans/:floorPlanId',
        element: <SiteWalkLayout />,
        children: [
            {
                index: true,
                element: <SiteWalkDetailPage />,
            },
            {
                path: 'compare',
                element: <SiteWalkComparePage />,
            },
            {
                path: 'bim-compare',
                element: <SiteWalkBimComparePage />,
            },
        ],
    },
    {
        path: 'projects/:projectId/live-cameras/:cameraId',
        element: <CameraCctvLayout />,
        children: [
            {
                index: true,
                element: <CctvDetailPage />,
            },
            {
                path: 'recordings',
                element: <CctvCameraRecordingListPage />,
            },
            {
                path: 'recordings/:recordingId/play',
                element: <CctvCameraRecordingDetailPage />,
            },
        ],
    },

    {
        path: 'projects/:projectId/drones/:droneId',
        element: <DroneDetailRouteRedirect />,
    },
    {
        path: 'projects/:projectId/aerial/drone/:droneId',
        element: <DroneDetailPage />,
    },
    {
        path: 'projects/:projectId/site-gallery/:itemId',
        element: <SiteGalleryDetailPage />,
    },
    {
        path: 'projects/:projectId/aerial/:aerialId',
        element: <OrthomosaicRouteRedirect />,
    },
    ...(import.meta.env.DEV ? [{
        path: 'projects/:projectId/aerial/local-tiles',
        element: <AerialLayout />,
        children: [{ index: true, element: <AerialLocalTilesPage /> }],
    }] : []),
    {
        path: 'projects/:projectId/aerial/terrain/:terrainId',
        element: <AerialLayout />,
        children: [
            {
                index: true,
                element: <AerialTerrainPage />,
            },
        ],
    },
    {
        path: 'projects/:projectId/aerial/orthomosaic/:orthomosaicId',
        element: <AerialLayout />,
        children: [
            {
                index: true,
                element: <AerialDetailPage />,
            },
            {
                path: 'compare',
                element: <AerialComparePage />,
            },
        ],
    },
];

export default Routes;
